import React, { useState, useEffect, useMemo, ReactNode } from "react";
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { Box } from "@mui/material";
import GenericCrudTableModal, {
  AdditionalComponentForModal,
} from "@/components/tables/genericCrudTableModal";
import { MRT_Localization_DE } from "material-react-table/locales/de";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { apiRequest } from "@/helper/api";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import { useDispatch } from "react-redux";
import { setTableSearch } from "@/components/state/tableSearch/tableSearchSlice";
import styles from "@/components/tables/genericCrudTable.module.css";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { setTableColumnFiltering } from "@/components/state/tableSearch/tableConfigSlice";
import { functionalUpdate } from "@tanstack/react-table";
import { setAdditionalFilteringParameters } from "@/components/state/tableSearch/tableConfigSlice";
import { getDefaultSort } from "@/components/tables/genericCrudTable.utils";
import { normalizeCustomFilterColumns } from "@/components/tables/genericCrudTableFilterColumnIntegration";
import MrtToolbarInternalActions from "@/components/tables/mrtToolbarInternalActions";
import { useTableNotifications } from "@/components/tables/hooks/useTableNotifications";
import { useColumnPinning } from "@/components/tables/hooks/useColumnPinning";
import { useTableSorting } from "@/components/tables/hooks/useTableSorting";
import { canEditUserRolesOnAdmin } from "@/helper/userRolePermissions";
import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import {
  buildCsvExportColumnOrdering,
  buildCsvExportFilters,
  type CsvExportHeaderDateFilter,
} from "@/components/tables/genericCrudTable.csvExportFilters";
import { buildCreatePayload } from "@/components/tables/genericCrudTable.createPayload";
import { buildUpdatePayload } from "@/components/tables/genericCrudTable.updatePayload";
import { useCrudModalState } from "@/components/tables/hooks/useCrudModalState";
import { useRowActionsColumnWidth } from "@/components/tables/hooks/useRowActionsColumnWidth";
import { useTableFetch } from "@/components/tables/hooks/useTableFetch";
import { useBulkActions } from "@/components/tables/hooks/useBulkActions";
import { useRowDelete } from "@/components/tables/hooks/useRowDelete";
import CrudTableTopToolbar from "@/components/tables/toolbar/CrudTableTopToolbar";
import RowActionItem, {
  type RowActionsLayout,
} from "@/components/tables/rowActions/RowActionItem";
import {
  getStandardRowActionDescriptors,
  type RowActionDescriptorContext,
  type RowActionHandlers,
} from "@/components/tables/rowActions/getRowActionDescriptors";
import {
  resolveSlotRowActions,
  type RowActionsSlot,
} from "@/components/tables/rowActions/rowActionsSlot";
import type { BulkActionsSlot } from "@/components/tables/toolbar/bulkActionsSlot";
import {
  getBodyCellSx,
  getColumnVisibilityHiddenByDefault,
  getRowBackgroundColor,
  getSearchFieldAutoFocus,
  getSearchFieldPlaceholder,
  getTableCellWhiteSpace,
} from "@/components/tables/genericCrudTable.presentation";
import { columnFilterStatesMatch } from "@/components/tables/mrtTableReset.utils";
import type { EntityAdapter } from "@/components/tables/entityAdapters/entityAdapter";

interface Props {
  entity: string;
  entityAdapter?: EntityAdapter;
  customEditHandler?: (row: any) => void;
  allowOpenEditInNewTab?: boolean;
  customDeleteHandler?: (row: any) => void;
  customCreateHandler?: () => void;
  uploadSuccessful?: boolean;
  companySubjectId?: string;
  companyId?: string;
  columns: CrudColumnDef[];
  disallowDelete?: boolean;
  disallowEdit?: boolean;
  additionalUrlParameter?: any;
  additionalRoutePartsForGet?: any;
  additionalRoutePartsForDelete?: any;
  searchId?: string;
  additionalColumnsForCreate?: any;
  additionalColumnsForEdit?: any;
  subjectIdForBuildings?: any;
  onOpenEdit?: (rowData: any) => void;
  refreshTable?: boolean;
  resetPaginationOnFilterChange?: boolean;
  readOnlyMode?: boolean;
  additionalComponentForModal?: AdditionalComponentForModal;
  customModalStyle?: any;
  useTwoColumnModalLayout?: boolean;
  expandedDepth?: boolean;
  customColumnVisibility?: any;
  customTopToolBarAction?: (data?: any) => ReactNode | undefined;
  customTopToolBarActionButton?: ReactNode | undefined;
  customGlobalFilterPosition?: any;
  customDensity?: "spacious" | "compact" | "comfortable";
  customPageSize?: number;
  maxRowsPerPageOption?: number;
  showRowActions?: boolean;
  customRowActions?: any;
  rowActions?: RowActionsSlot;
  bulkActions?: BulkActionsSlot;
  tableCollapseWidth?: any;
  allowColumnFiltering?: boolean;
  showColumnFilterbyDefault?: boolean;
  customCreatePresetValues?: any;
  additionalSubmitDataForCreate?: any;
  additionalOnSuccessfulCreateFunction?: any;
  additionalOnSuccessfulEditFunction?: any;
  hideDeleteButtonForRows?: boolean;
  hideEditButtonForRows?: boolean;
  disableEditButtonForRow?: (row: any) => boolean;
  disableDeleteButtonForRow?: (row: any) => boolean;
  getDeleteButtonDisabledTooltip?: (row: any) => string | undefined;
  getBulkDeleteDisabledTooltip?: (selectedRows: any[]) => string | undefined;
  disableGlobalFilter?: boolean;
  disableGlobalFilterMatchHighlighting?: boolean;
  rightAlignTopToolbarActions?: boolean;
  customRowActionsLayout?: "buttons" | "menu";
  customDetailPanel?: any;
  customTableData?: any;
  disableAddItem?: boolean;
  refreshTableAfterAction?: () => void;
  additionalKeyForParameteresToRender?: any;
  onSuccessfulGetHandler?: (data: any) => void;
  transformGetResults?: (results: any[]) => any[];
  additionalCustomTopToolbarElements?: any;
  additionalCustomTopToolbarElementsForRowSelection?: any;
  enableRowSelection?: boolean | ((row: any) => boolean);
  enableBulkDelete?: boolean | ((selectedRows: any[]) => boolean);
  csvExportUrl?: string | null;
  csvExportHeaderDateFilter?: CsvExportHeaderDateFilter;
  resetRowSelection?: boolean;
  additionalRowsPerPageOptions?: number[];
  enablePagination?: boolean;
  allowPaginationWithCustomTableData?: boolean;
  enableSortingWithCustomTableData?: boolean;
  enableColumnFilteringWithCustomTableData?: boolean;
  hasAdditionalFiltersToReset?: boolean;
  onResetAllFilters?: () => void;
  initialColumnFilters?: MRT_ColumnFiltersState;
  disablePersistedColumnFilters?: boolean;
  dropFiltersForRemovedColumns?: boolean;
  customRowActionColSize?: number;
  onSuccessfullPostCallback?: (data: any) => void;
  onExportParamsChange?: (params: {
    globalFilter?: string;
    columnFilters?: any[];
    additionalUrlParameters?: any;
    ordering?: string;
  }) => void;
  elementBelowGlobalSearchInput?: (rowCount: number) => ReactNode;
  remountCrudModalOnPresetChange?: boolean;
  editDisplayModeOverride?: "modal" | "cell";
  displayDataTransform?: (rows: any[]) => any[];
  tableInstanceRef?: React.MutableRefObject<{
    setEditingCell: (cell: any) => void;
  } | null>;
  bottomToolbarPaginationAlign?: "flex-start" | "center" | "flex-end";
  dataTestId?: string; // used for cypress e2e tests
  shouldNotImpersonate?: boolean;
  columnMinSize?: number;
}

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [
  5, 10, 15, 20, 25, 30, 50, 100, 150, 200, 300, 400, 500,
];

const isFullnameConflictError = (error: any) => {
  const code = Array.isArray(error?.code) ? error.code[0] : error?.code;
  return code === "fullname_conflict";
};

export default function GenericCrudTable(props: Props) {
  const { entityAdapter } = props;
  const persistsTableStateInRedux =
    entityAdapter?.persistsTableStateInRedux ?? false;
  const providesColumnFiltersToCustomAction =
    entityAdapter?.providesColumnFiltersToCustomAction ?? false;
  const persistsGlobalSearch = entityAdapter?.persistsGlobalSearch ?? true;
  const confirmsFullnameConflict =
    entityAdapter?.confirmsFullnameConflict ?? false;
  const runsAdditionalCreateCallbackOnCreate =
    entityAdapter?.runsAdditionalCreateCallbackOnCreate ?? false;
  const {
    modalOpen,
    modalMode,
    modalPresetValues,
    openCreateModal,
    openEditModal,
    closeModal,
  } = useCrudModalState({
    columns: props.columns,
    customCreatePresetValues: props.customCreatePresetValues,
    entityAdapter,
  });
  const { handleError, errorModal } = useErrorModal();
  const {
    successForNotification,
    notificationMessage,
    notificationVariant,
    notify,
    resetStateOnCloseNotification,
  } = useTableNotifications();
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: props.customPageSize ? props.customPageSize : 50,
  });
  const [globalFilter, setGlobalFilter] = useState("");
  const [initialColumnFiltersSnapshot] = useState<MRT_ColumnFiltersState>(() =>
    cloneDeep(props.initialColumnFilters ?? []),
  );
  const [columnFilter, setColumnFilter] = useState<MRT_ColumnFiltersState>(() =>
    cloneDeep(initialColumnFiltersSnapshot),
  );
  const {
    sorting,
    setSorting,
    columnSorting,
    setColumnSorting,
    sortingReady,
    isInitialSortRef,
  } = useTableSorting({ entity: props.entity, entityAdapter });
  const [rowSelection, setRowSelection] = useState({});
  const { columnPinning, columnPinningFromColumns, handleColumnPinningChange } =
    useColumnPinning({
      columns: props.columns,
      entity: props.entity,
      enableRowSelection: props.enableRowSelection,
      showRowActions: props.showRowActions,
    });
  const [columnVisibility, setColumnVisibility] = useState({
    id: false,
    created_by: false,
    modified_by: false,
    modified_on: false,
    ...props.customColumnVisibility,
  });
  const columnVisibilityWithColumnDefaults = {
    ...getColumnVisibilityHiddenByDefault(props.columns),
    ...columnVisibility,
  };

  const { tableRows, rowCount, emailsOnPage, requestReload } = useTableFetch({
    entity: props.entity,
    entityAdapter,
    columns: props.columns,
    columnFilters: columnFilter,
    globalFilter,
    ordering: columnSorting,
    sorting,
    pagination,
    searchId: props.searchId,
    companySubjectId: props.companySubjectId,
    additionalUrlParameter: props.additionalUrlParameter,
    additionalRoutePartsForGet: props.additionalRoutePartsForGet,
    additionalKeyForParameteresToRender:
      props.additionalKeyForParameteresToRender,
    expandedDepth: props.expandedDepth,
    shouldNotImpersonate: props.shouldNotImpersonate,
    refreshTable: props.refreshTable,
    resetPaginationOnFilterChange: props.resetPaginationOnFilterChange,
    onResetPagination: () =>
      setPagination((previous) => ({ ...previous, pageIndex: 0 })),
    customTableData: props.customTableData,
    enableColumnFilteringWithCustomTableData:
      props.enableColumnFilteringWithCustomTableData,
    enableSortingWithCustomTableData: props.enableSortingWithCustomTableData,
    allowPaginationWithCustomTableData:
      props.allowPaginationWithCustomTableData,
    transformGetResults: props.transformGetResults,
    onSuccessfulGet: props.onSuccessfulGetHandler,
    onExportParamsChange: props.onExportParamsChange,
    onError: handleError,
    orderingReady: sortingReady,
    reloadOn: [props.companySubjectId, props.uploadSuccessful],
  });

  const { rowActionsColumnSize, measureActionsCell, resetMeasurements } =
    useRowActionsColumnWidth(props.customRowActionColSize);
  const globalSubject = useSelector((state: RootState) => state.subjectSearch);

  const { showConfirmationModalAsync } = useConfirmationModal();

  const mergedRowsPerPageOptions = Array.from(
    new Set([
      ...DEFAULT_ROWS_PER_PAGE_OPTIONS,
      ...(props.additionalRowsPerPageOptions ?? []).map(Number),
    ]),
  )
    .filter(
      (option) =>
        !props.maxRowsPerPageOption || option <= props.maxRowsPerPageOption,
    )
    .sort((a, b) => a - b);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedChildCompany: any = useSelector(
    (state: RootState) => state.parent,
  );
  const user = useSelector((state: RootState) => state.login);

  const tableSearchState: any = useSelector(
    (state: RootState) => state.tableSearch[props.entity],
  );
  const logedInUserInfo: any = useSelector((state: RootState) => state.login);
  const globalTableConfig: any =
    useSelector((state: RootState | any) => state.tableConfig) ?? {};

  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];
  const canEditUserRoles = canEditUserRolesOnAdmin(subdomain, user?.permissions);
  const canUseColumnFilters =
    props.allowColumnFiltering ??
    (subdomain === "admin" || subdomain === "manage");

  const data = useMemo(
    () =>
      props.displayDataTransform
        ? props.displayDataTransform(tableRows)
        : tableRows,
    [tableRows, props.displayDataTransform],
  );
  const normalizedColumns = useMemo(
    () =>
      normalizeCustomFilterColumns(props.columns || [], {
        columnMinSize: props.columnMinSize,
        allowsDateRangeFilters: entityAdapter?.allowsDateRangeFilters,
      }),
    [props.columnMinSize, props.columns, entityAdapter],
  );

  const columns = normalizedColumns as any;

  useEffect(() => {
    if (tableSearchState && tableSearchState !== undefined) {
      setGlobalFilter(tableSearchState);
    } else setGlobalFilter("");
  }, []);

  useEffect(() => {
    if (isInitialSortRef.current) {
      isInitialSortRef.current = false;
      return;
    }
    if (sorting.length !== 0) {
      const sortId = sorting[0].id;
      const isDescending = sorting[0].desc === true;

      const sortCol = columns.find((col: any) => col.id === sortId);

      const sortingkey = sortCol?.customSortingKey || sortId;

      const descendingPrefix = isDescending ? "-" : "";

      setColumnSorting(`${descendingPrefix}${sortingkey}`);
      if (persistsTableStateInRedux) {
        dispatch(
          setAdditionalFilteringParameters({
            entity: props.entity,
            filters: {
              sorting: {
                queryParameter: `${descendingPrefix}${sortingkey}`,
                table: { id: sortId, desc: isDescending },
              },
            },
          }),
        );
      }
    } else {
      const defaultSort = getDefaultSort(entityAdapter);
      setColumnSorting(defaultSort.sort);
      if (persistsTableStateInRedux) {
        dispatch(
          setAdditionalFilteringParameters({
            entity: props.entity,
            filters: {
              sorting: null,
            },
          }),
        );
      }
    }
  }, [sorting, props.entity]);

  const currentColumnIds = useMemo(
    () =>
      (props.columns || [])
        .map((c: any) => c.id)
        .filter(
          (id: unknown): id is string | number =>
            id != null && (typeof id === "string" || typeof id === "number"),
        ),
    [props.columns],
  );
  const currentColumnIdsKey = useMemo(
    () => JSON.stringify(currentColumnIds),
    [currentColumnIds],
  );
  useEffect(() => {
    if (!props.dropFiltersForRemovedColumns) return;
    setColumnFilter((prev: any[]) => {
      const next = prev.filter((f: any) => currentColumnIds.includes(f.id));
      return next.length === prev.length ? prev : next;
    });
  }, [currentColumnIdsKey, props.dropFiltersForRemovedColumns]);

  useEffect(() => {
    resetMeasurements();
  }, [
    resetMeasurements,
    pagination.pageIndex,
    pagination.pageSize,
    tableRows,
    sorting,
    columnFilter,
    globalFilter,
  ]);

  const handleOpenEdit = (row: any) => {
    if (props.customEditHandler) {
      props.customEditHandler(row);
    } else {
      openEditModal(row.original);
    }
  };
  const handleOpenCreate = () => {
    if (props.customCreateHandler) {
      props.customCreateHandler();
    } else openCreateModal();
  };

  useEffect(() => {
    setRowSelection({});
  }, [props.refreshTable, props.resetRowSelection]);

  const { deleteRow } = useRowDelete({
    entity: props.entity,
    entityAdapter,
    companySubjectId: props.companySubjectId,
    refreshTableAfterAction: props.refreshTableAfterAction,
    notify,
    requestReload,
    onError: handleError,
  });

  const isEditDisabledForRow = (row: any) =>
    Boolean(props.disallowEdit) ||
    Boolean(props.disableEditButtonForRow?.(row));

  const buildRowActionHandlers = (
    row: any,
    closeMenu?: () => void,
  ): RowActionHandlers => {
    const thenCloseMenu = (action: () => void) => () => {
      action();
      closeMenu?.();
    };

    const openEdit = thenCloseMenu(() => {
      handleOpenEdit(row);
      props.onOpenEdit?.(row.original);
    });

    return {
      edit: (event) => {
        if (isEditDisabledForRow(row)) return;
        if (
          props.allowOpenEditInNewTab &&
          props.customEditHandler &&
          (event.ctrlKey || event.metaKey)
        ) {
          event.preventDefault();
          props.customEditHandler({ ...row, allowOpenInNewTab: true });
          closeMenu?.();
          return;
        }
        openEdit();
      },
      delete: thenCloseMenu(() => {
        if (props.customDeleteHandler) {
          props.customDeleteHandler(row);
        } else {
          deleteRow(row);
        }
      }),
    };
  };

  const getRowActions = (row: any, closeMenu?: () => void) => {
    const layout: RowActionsLayout =
      props.customRowActionsLayout === "menu" ? "menu" : "buttons";
    const descriptorContext: RowActionDescriptorContext = {
      visibility: rowVisibilityContext,
      isEditDisabled: isEditDisabledForRow(row),
      getDeleteButtonDisabledTooltip: props.getDeleteButtonDisabledTooltip,
      deletesPermanently:
        entityAdapter?.deletesPermanently?.(user.email) ?? false,
      handlers: buildRowActionHandlers(row, closeMenu),
      t,
    };
    const standardActions = getStandardRowActionDescriptors(
      row,
      descriptorContext,
    );
    const descriptors = props.rowActions
      ? resolveSlotRowActions(props.rowActions, row, {
          standardActions,
          requestReload,
          clearSelection: () => setRowSelection({}),
          closeMenu,
        })
      : standardActions;
    const rowActions: ReactNode[] = descriptors.map((action) => (
      <RowActionItem key={action.key} action={action} layout={layout} />
    ));
    if (props.customRowActions) {
      rowActions.push(props.customRowActions(row));
    }
    return rowActions;
  };

  const renderRowActionsAsButtons = (row: any) => {
    const actions = getRowActions(row).map((action, index) => (
      <React.Fragment key={index}>{action}</React.Fragment>
    ));

    return (
      <Box
        ref={(el) =>
          measureActionsCell(
            row.id ?? String(row.index),
            el as HTMLElement | null,
          )
        }
        className={styles.rowActionsWrapper}
      >
        {actions}
      </Box>
    );
  };

  const renderRowActionsAsMenu = (row: any, closeMenu: () => void) => {
    const actions = getRowActions(row, closeMenu).map((action) => action);
    return actions;
  };

  const handleCreateNewRow = async (
    values: any,
    resetFunction?: () => void,
  ) => {
    const result = buildCreatePayload(values, {
      columns: props.columns,
      subdomain,
      canEditUserRoles,
      companySubjectId: props.companySubjectId,
      companyId: props.companyId,
      selectedChildCompanySubjectId: selectedChildCompany.subjectId,
      globalSubjectValue: globalSubject?.value,
      currentUserSubjectId: user?.subjectId,
      searchId: props.searchId,
      additionalSubmitDataForCreate: props.additionalSubmitDataForCreate,
      entityAdapter,
    });
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    const submitCreateRequest = (ignoreFullnameConflict: boolean) =>
      apiRequest({
        method: "post",
        entity: entityAdapter?.createEntity ?? props.entity,
        submitData: confirmsFullnameConflict
          ? {
              ...result,
              ignore_fullname_conflict: ignoreFullnameConflict,
            }
          : result,
        config: config,
      });

    const handleSuccessfulCreateResponse = (response: any) => {
      notify(t("create_successful"), "success");
      closeModal();
      requestReload();

      if (resetFunction) {
        resetFunction();
      }
      props?.onSuccessfullPostCallback?.(response.data);
      if (
        props.additionalOnSuccessfulCreateFunction &&
        runsAdditionalCreateCallbackOnCreate
      ) {
        props.additionalOnSuccessfulCreateFunction(response.data, values);
      }
      if (props.refreshTableAfterAction) {
        props.refreshTableAfterAction();
      }
    };

    try {
      const createConfirmed =
        (await entityAdapter?.confirmCreate?.(result, {
          confirm: (messageKey) => showConfirmationModalAsync(t(messageKey)),
        })) ?? true;
      if (!createConfirmed) {
        return;
      }

      const response = await submitCreateRequest(false);
      handleSuccessfulCreateResponse(response);
    } catch (error) {
      if (confirmsFullnameConflict && isFullnameConflictError(error)) {
        const isConfirmed = await showConfirmationModalAsync(
          t("fullname_conflict_confirmation"),
        );
        if (!isConfirmed) {
          return;
        }

        try {
          const response = await submitCreateRequest(true);
          handleSuccessfulCreateResponse(response);
        } catch (retryError) {
          handleError(retryError);
        }
        return;
      }

      handleError(error);
    }
  };

  const handleSaveRow = async (values: any, resetFunction?: () => void) => {
    const result = buildUpdatePayload(values, {
      columns: props.columns,
      subdomain,
      canEditUserRoles,
      companySubjectId: props.companySubjectId,
      companyId: props.companyId,
      selectedChildCompanySubjectId: selectedChildCompany.subjectId,
      searchId: props.searchId,
      presetValues: modalPresetValues,
      entityAdapter,
    });

    const saveBlockReason = entityAdapter?.getSaveBlockReason?.(
      result,
      modalPresetValues,
    );
    if (saveBlockReason) {
      notify(t(saveBlockReason), "error");
      return;
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    try {
      const response = await apiRequest({
        entity: props.entity,
        method: "put",
        entityId: String(modalPresetValues.id),
        submitData: result,
        config: config,
      });

      notify(t("save_successful"), "success");
      closeModal();
      requestReload();
      if (resetFunction) {
        resetFunction();
      }
      if (props.additionalOnSuccessfulCreateFunction) {
        props.additionalOnSuccessfulCreateFunction(response.data);
      }
      if (props.additionalOnSuccessfulEditFunction) {
        props.additionalOnSuccessfulEditFunction(response.data, values);
      }
      if (props.refreshTableAfterAction) {
        props.refreshTableAfterAction();
      }
    } catch (error) {
      handleError(error);
    }
  };

  const { bulkDelete } = useBulkActions({
    entity: props.entity,
    companySubjectId: props.companySubjectId,
    refreshTableAfterAction: props.refreshTableAfterAction,
    notify,
    requestReload,
    clearRowSelection: () => setRowSelection({}),
    onError: handleError,
  });

  const rowVisibilityContext = {
    subdomain,
    hideEditButtonForRows: props.hideEditButtonForRows,
    hideDeleteButtonForRows: props.hideDeleteButtonForRows,
    disallowDelete: props.disallowDelete,
    disableDeleteButtonForRow: props.disableDeleteButtonForRow,
    entityAdapter,
  };

  let enableEditing = true;
  let enableRowActions = true;

  if (
    (props.readOnlyMode && props.readOnlyMode == true) ||
    props.showRowActions === false
  ) {
    enableEditing = false;
    enableRowActions = false;
  }

  if (props.customRowActions && props.showRowActions !== false) {
    enableRowActions = true;
  }

  if (props.customRowActionsLayout === "menu") {
    enableEditing = false;
  }

  if (props.editDisplayModeOverride === "cell") {
    enableEditing = true;
  }

  const handleSetGlobalTableSearch = (searchValue: string, entity: string) => {
    if (!persistsGlobalSearch) return;

    dispatch(setTableSearch({ searchValue: searchValue, entity: entity }));
  };

  const handleColumnFilter = (updater: any, currentFilters: any) => {
    const newFilters = functionalUpdate(updater, currentFilters);

    //currently only dispachtes on trackesService. if we decide to store all entityColumnSearch
    //just delete the check
    if (persistsTableStateInRedux && !props.disablePersistedColumnFilters) {
      dispatch(
        setTableColumnFiltering({
          entity: props.entity,
          filters: newFilters,
        }),
      );
    }
    setColumnFilter(newFilters);
  };

  const defaultResetSort = getDefaultSort(entityAdapter);
  const hasGlobalFilterToReset =
    typeof globalFilter === "string"
      ? globalFilter !== ""
      : Boolean(globalFilter);
  const hasColumnFiltersToReset = !columnFilterStatesMatch(
    columnFilter,
    initialColumnFiltersSnapshot,
  );
  const hasSortingToReset =
    !isInitialSortRef.current &&
    (sorting.length !== 1 ||
      sorting[0].id !== defaultResetSort.tableSort ||
      sorting[0].desc !== defaultResetSort.descending);
  const hasFiltersToReset =
    hasGlobalFilterToReset ||
    hasColumnFiltersToReset ||
    hasSortingToReset ||
    Boolean(props.hasAdditionalFiltersToReset);

  const handleResetAllFilters = () => {
    setGlobalFilter("");
    handleSetGlobalTableSearch("", props.entity);
    handleColumnFilter(cloneDeep(initialColumnFiltersSnapshot), columnFilter);
    setSorting([
      { id: defaultResetSort.tableSort, desc: defaultResetSort.descending },
    ]);
    setColumnSorting(defaultResetSort.sort);
    props.onResetAllFilters?.();
  };

  let enableGlobalFilter = true;
  if (props.disableGlobalFilter) {
    enableGlobalFilter = false;
  }
  const useManualSorting =
    !props.customTableData || Boolean(props.enableSortingWithCustomTableData);

  const enrichedColumnFiltersForCsvExport = buildCsvExportFilters(
    columnFilter,
    props.columns,
    entityAdapter?.allowsDateRangeFilters ?? true,
    props.csvExportHeaderDateFilter,
  );

  const bulkActionsSlot = props.bulkActions;
  const getBulkActionDescriptors = bulkActionsSlot
    ? (selectedRows: any[]) =>
        bulkActionsSlot(selectedRows, {
          clearSelection: () => setRowSelection({}),
          requestReload,
        })
    : undefined;

  let customTopToolBarActionReturnValue: any = null;
  if (providesColumnFiltersToCustomAction) {
    customTopToolBarActionReturnValue = columnFilter;
  }

  const table = useMaterialReactTable<any>({
    columns: normalizedColumns,
    muiPaginationProps: { rowsPerPageOptions: mergedRowsPerPageOptions },
    data,
    editDisplayMode: props.editDisplayModeOverride ?? "modal",
    localization:
      logedInUserInfo.preferred_language === "en"
        ? MRT_Localization_EN
        : MRT_Localization_DE,
    enableSorting: true,
    enableStickyHeader: true,
    layoutMode: "semantic",
    onColumnVisibilityChange: setColumnVisibility,
    displayColumnDefOptions: {
      "mrt-row-actions": {
        visibleInShowHideMenu: true,
        size: rowActionsColumnSize,
        minSize: rowActionsColumnSize,
        muiTableHeadCellProps: {
          align: "left",
          sx: {
            padding: "0.5rem",
            width: rowActionsColumnSize,
            minWidth: rowActionsColumnSize,
            maxWidth: rowActionsColumnSize,
          },
        },
        muiTableBodyCellProps: {
          sx: {
            padding: "0.5rem",
            textAlign: "left",
            borderRight: "1px solid #e0e0e0",
            width: rowActionsColumnSize,
            minWidth: rowActionsColumnSize,
            maxWidth: rowActionsColumnSize,
            "& .MuiIconButton-root": {
              margin: 0,
            },
          },
        },
      },
      "mrt-row-select": {
        visibleInShowHideMenu: true,
        size: 40,
        minSize: 50,
        muiTableHeadCellProps: {
          align: "center",
          sx: {
            padding: "0.5rem",
          },
        },
        muiTableBodyCellProps: {
          sx: {
            padding: "0.5rem",
            textAlign: "center",

            borderRight: "1px solid #e0e0e0",
            "& .MuiIconButton-root": {
              margin: 0,
            },
          },
        },
      },
      "mrt-row-expand": { visibleInShowHideMenu: false },
    },
    positionGlobalFilter:
      props.customGlobalFilterPosition && props.customGlobalFilterPosition,
    muiTopToolbarProps: {
      sx: { flexShrink: 0 },
    },
    muiTableContainerProps: {
      sx: {
        flex: "1 1 auto",
        minHeight: 0,
        maxHeight: "none", // the parent layout determines whether the table or page scrolls
        "& .MuiTableBody-root .MuiTableCell-stickyLeft, & .MuiTableBody-root .MuiTableCell-stickyRight":
          {
            zIndex: 3,
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        "& .MuiTableHead-root .MuiTableCell-stickyLeft, & .MuiTableHead-root .MuiTableCell-stickyRight":
          {
            zIndex: 7,
            backgroundColor: (theme) => theme.palette.background.paper,
          },
      },
    },
    muiTableHeadProps: {
      sx: {
        position: "sticky",
        top: 0,
        zIndex: 6,
      },
    },
    muiTableProps: {
      sx: {
        tableLayout: "fixed",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        minWidth: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        px: 1,
        "&.MuiTableCell-stickyLeft": {
          zIndex: 7,
          backgroundColor: (theme) => theme.palette.background.paper,
        },
        "&.MuiTableCell-stickyRight": {
          zIndex: 7,
          backgroundColor: (theme) => theme.palette.background.paper,
        },
      },
    },
    enableEditing,
    enableHiding: true,
    enableColumnResizing: true,
    enableFullScreenToggle: false,
    enableRowActions,
    manualFiltering: true,
    enableGlobalFilter,
    enableFilterMatchHighlighting: !props.disableGlobalFilterMatchHighlighting,
    onGlobalFilterChange: (e: any) => {
      setGlobalFilter(e);
      handleSetGlobalTableSearch(e, props.entity);
    },
    onColumnFiltersChange: (updater: any) => {
      handleColumnFilter(updater, columnFilter);
    },
    manualPagination: true,
    manualSorting: useManualSorting,
    enableColumnOrdering: false,
    positionToolbarDropZone: "none",
    muiBottomToolbarProps: (() => {
      const align = props.bottomToolbarPaginationAlign;
      if (!align || align === "flex-end") return { sx: { flexShrink: 0 } };
      return {
        sx: {
          flexShrink: 0,
          height: 56,
          minHeight: 56,
          maxHeight: 56,
          alignItems: "center",
          "& > div:last-child": {
            alignSelf: "stretch",
            justifyContent: align,
            alignItems: "center",
          },
          "& > div:last-child > div:last-child": {
            position: "absolute",
            top: "50%",
            left: 0,
            right: align === "center" ? 0 : "auto",
            transform: "translateY(-50%)",
            width: align === "center" ? "100%" : "auto",
            justifyContent: align,
            display: "flex",
            alignItems: "center",
          },
          "& .MuiTablePagination-root": {
            margin: 0,
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
      };
    })(),
    onPaginationChange: (e: any) => {
      setPagination(e);
    },
    rowCount,
    onSortingChange: (e: any) => {
      setSorting(e);
    },
    enableColumnFilters: canUseColumnFilters,
    enableColumnPinning: true,
    muiSearchTextFieldProps: {
      InputProps: {
        sx: {
          borderRadius: "0.75rem",
        },
      },
      onClick: () => {
        setGlobalFilter("");
      },

      placeholder: getSearchFieldPlaceholder(t, entityAdapter),
      autoFocus: getSearchFieldAutoFocus(entityAdapter),
      ...(props.elementBelowGlobalSearchInput
        ? {
            helperText: props.elementBelowGlobalSearchInput(rowCount),
            FormHelperTextProps: {
              component: "div", 
              sx: {
                display: "flex",
                justifyContent: "flex-end",
                marginRight: 0,
              },
            },
          }
        : {}),
    },
    muiTableBodyRowProps: ({ row }: any) => ({
      "data-testid": props.dataTestId
        ? `${props.dataTestId}-row-${row.original.id}`
        : undefined,
      sx: {
        backgroundColor: getRowBackgroundColor(row, entityAdapter),
      },
    }),
    muiTablePaperProps: {
      sx: {
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        marginBottom: "2rem",
        "& .MuiTextField-root": {
          backgroundColor: "#f4f2f2",
        },
        "&&  .MuiCollapse-root, && .MuiCollapse-wrapperInner": {
          width: props.tableCollapseWidth && props.tableCollapseWidth,
        },
        "&& .MuiTableCell-root": {
          whiteSpace: getTableCellWhiteSpace(entityAdapter),
        },
        "&& .MuiTextField-root": {
          backgroundColor: "white",
        },
        "&& .MuiBox-root": {
          zIndex: "unset",
        },
      },
    },
    muiTableBodyCellProps: ({ row, cell, column, table }: any) => {
      const baseSx = {
        minWidth: 0,
        overflowWrap: "anywhere",
        px: 1,
        borderRight: "1px solid #e0e0e0",
      };

      const result: { sx: any; onClick?: () => void } = {
        sx: getBodyCellSx(row.original, baseSx, entityAdapter),
      };

      if (
        props.editDisplayModeOverride === "cell" &&
        table &&
        cell &&
        column?.columnDef?.enableEditing !== false
      ) {
        result.onClick = () => {
          table.setEditingCell(cell);
          queueMicrotask(() => {
            const textField = table.refs?.editInputRefs?.current?.[column.id];
            if (textField) {
              textField.focus();
              const len = textField.value?.length ?? 0;
              textField.setSelectionRange(len, len);
            }
          });
        };
      }

      return result;
    },
    initialState: {
      columnVisibility: columnVisibilityWithColumnDefaults,
      showGlobalFilter: true,
      showColumnFilters: canUseColumnFilters
        ? !!props.showColumnFilterbyDefault
        : false,
      density: props.customDensity ? props.customDensity : "comfortable",
      columnPinning: columnPinningFromColumns,
    },
    state: {
      pagination,
      globalFilter,
      columnFilters: columnFilter,
      sorting,
      rowSelection,
      columnPinning,
      columnVisibility: columnVisibilityWithColumnDefaults,
    },
    onColumnPinningChange: handleColumnPinningChange,
    renderRowActions:
      !props.customRowActionsLayout ||
      props.customRowActionsLayout === "buttons"
        ? ({ row }: any) => renderRowActionsAsButtons(row)
        : undefined,
    renderRowActionMenuItems:
      props.customRowActionsLayout === "menu"
        ? ({ row, closeMenu }: any) => renderRowActionsAsMenu(row, closeMenu)
        : undefined,
    enableRowSelection: props.enableRowSelection,
    onRowSelectionChange: (e: any) => {
      setRowSelection(e);
    },
    enablePagination: props.enablePagination !== false,
    onEditingRowSave: handleSaveRow,
    renderToolbarInternalActions: ({ table }) => (
      <MrtToolbarInternalActions
        table={table}
        showReset={hasFiltersToReset}
        onReset={handleResetAllFilters}
      />
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <CrudTableTopToolbar
        table={table}
        entityAdapter={entityAdapter}
        subdomain={subdomain}
        readOnlyMode={props.readOnlyMode}
        dataTestId={props.dataTestId}
        rightAlignTopToolbarActions={props.rightAlignTopToolbarActions}
        disableAddItem={props.disableAddItem}
        enableRowSelection={props.enableRowSelection}
        enableBulkDelete={props.enableBulkDelete}
        getBulkDeleteDisabledTooltip={props.getBulkDeleteDisabledTooltip}
        customTopToolBarAction={props.customTopToolBarAction}
        customTopToolBarActionValue={customTopToolBarActionReturnValue}
        customTopToolBarActionButton={props.customTopToolBarActionButton}
        additionalCustomTopToolbarElements={
          props.additionalCustomTopToolbarElements
        }
        additionalCustomTopToolbarElementsForRowSelection={
          props.additionalCustomTopToolbarElementsForRowSelection
        }
        csvExportUrl={props.csvExportUrl}
        csvExport={{
          additionalFilter: enrichedColumnFiltersForCsvExport,
          additionalSorting: sorting,
          columnOrdering: buildCsvExportColumnOrdering(
            table.getVisibleLeafColumns().map((column) => column.id),
            columnVisibilityWithColumnDefaults,
          ),
          handleError,
        }}
        onOpenCreate={handleOpenCreate}
        bulkDelete={bulkDelete}
        getBulkActionDescriptors={getBulkActionDescriptors}
      />
    ),
    ...(props.customDetailPanel && {
      renderDetailPanel: ({ row }) => props.customDetailPanel(row),
    }),
  });

  if (props.tableInstanceRef) {
    props.tableInstanceRef.current = {
      setEditingCell: table.setEditingCell,
    };
  }

  useEffect(() => {
    //currently only for TrackedService
    if (!persistsTableStateInRedux) return;
    if (props.disablePersistedColumnFilters) return;

    if (!table.getAllLeafColumns().length) return;

    const entityData = globalTableConfig[props.entity];
    const filtersForEntity = entityData?.columnFilters;

    if (filtersForEntity) {
      try {
        const parsedFilters =
          typeof filtersForEntity === "string"
            ? JSON.parse(filtersForEntity)
            : filtersForEntity;

        if (parsedFilters && Array.isArray(parsedFilters)) {
          setColumnFilter(parsedFilters);
        }
      } catch (error) {
        console.warn("Failed to parse persisted column filter state:", error);
      }
    }
  }, [props.entity, globalTableConfig, table.getAllLeafColumns().length]);

  return (
    <>
      <MaterialReactTable table={table} />

      <>
        {(() => {
          const presetId = (modalPresetValues as any)?.id ?? "new";
          const modalMountKey = `${modalMode}-${String(presetId)}`;
          const genericCrudTableModalProps = {
            columns: props.columns,
            additionalComponentForModal: props.additionalComponentForModal,
            subjectIdForBuildings: props.subjectIdForBuildings,
            additionalColumnsForCreate: props.additionalColumnsForCreate,
            additionalColumnsForEdit: props.additionalColumnsForEdit,
            companySubjectId: props.companySubjectId,
            companyId: props.companyId,
            open: modalOpen,
            presetValues: modalPresetValues,
            modalMode: modalMode,
            customModalStyle: props.customModalStyle,
            useTwoColumnModalLayout: props.useTwoColumnModalLayout,
            userEmails: emailsOnPage,
            entityOfTable: props.entity,
            entityAdapter,
            handleError: handleError,
            onClose: () => {
              closeModal();
            },
            title: (function () {
              if (modalMode === "edit") {
                return t("edit_element");
              }
              return t("add_new_element");
            })(),
            submitButtonText: (function () {
              if (modalMode === "edit") {
                return t("save");
              }
              return t("add");
            })(),
            onSubmit: (function () {
              if (modalMode === "edit") {
                return handleSaveRow;
              }
              return handleCreateNewRow;
            })(),
          } as const;

          return (
            <GenericCrudTableModal
              {...genericCrudTableModalProps}
              {...(props.remountCrudModalOnPresetChange
                ? { key: modalMountKey }
                : {})}
            />
          );
        })()}

        {errorModal}
      </>

      <GenericNotification
        message={successForNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </>
  );
}
